import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Clock, BicepsFlexed, Flame, type LucideIcon } from "lucide-react";
import { cn } from "../lib/utils";
import type { ExerciseLogEntry } from "../utils/exerciseLogHistory";
import { exerciseTheme, getTypeMeta } from "../utils/manualExerciseUi";
import {
  measureActionLabel,
  measureCardHeading,
  measureCardMeta,
  measureCardTitle,
  measureCardValueSubhead,
  measureEyebrow,
} from "../utils/gluceraTypography";

export type ManualExerciseLogCardProps = {
  className?: string;
  exercises?: ExerciseLogEntry[];
  loading?: boolean;
  onLog: () => void;
  logDisabled?: boolean;
};

const LIST_PREVIEW_LIMIT = 3;

const ManualExerciseLogCard = ({
  className,
  exercises = [],
  loading = false,
  onLog,
  logDisabled = false,
}: ManualExerciseLogCardProps) => {
  const [showAll, setShowAll] = useState(false);
  const count = exercises.length;
  const hasMore = count > LIST_PREVIEW_LIMIT;
  const visibleExercises =
    hasMore && !showAll ? exercises.slice(0, LIST_PREVIEW_LIMIT) : exercises;
  const hiddenCount = count - LIST_PREVIEW_LIMIT;

  useEffect(() => {
    if (count <= LIST_PREVIEW_LIMIT) setShowAll(false);
  }, [count]);

  return (
    <div className={cn("space-y-2", className)}>
      <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60">
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b",
            exerciseTheme.cardGradient
          )}
          aria-hidden
        />

        <button
          type="button"
          className="relative flex w-full items-start gap-3 p-4 text-left outline-none ring-offset-background transition-colors hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onLog}
        >
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
              exerciseTheme.iconWrap
            )}
          >
            <BicepsFlexed className={cn("h-[22px] w-[22px]", exerciseTheme.icon)} strokeWidth={2} />
          </div>

          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className={measureCardHeading}>Log Manual Exercise</span>
              <span className={cn(measureCardValueSubhead, exerciseTheme.estimateText)}>
                {loading ? "…" : count > 0 ? `${count} today` : "—"}
              </span>
            </div>
            <p className={cn("mt-1.5", measureCardMeta)}>Track any workout you did</p>
          </div>
        </button>

        <div className="border-t border-dashed border-border px-4" aria-hidden />
        <button
          type="button"
          disabled={logDisabled}
          className={cn(
            "flex w-full flex-col items-center gap-2 py-4 outline-none transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50",
            measureActionLabel
          )}
          onClick={onLog}
        >
          <Clock className="h-5 w-5 text-muted-foreground" strokeWidth={1.85} aria-hidden />
          Log exercise
        </button>
      </div>

      {loading ? (
        <p className={cn("px-0.5", measureEyebrow)}>Loading today&apos;s workouts…</p>
      ) : count > 0 ? (
        <div className="space-y-2">
          <p className={cn("px-0.5", measureEyebrow)}>Today&apos;s logged</p>
          <ul className="space-y-2">
            {visibleExercises.map((ex) => {
              const t = getTypeMeta(ex.type);
              const Icon = t.icon as LucideIcon;
              return (
                <li
                  key={ex.id}
                  className={cn(
                    "flex items-center gap-3 rounded-xl border px-3 py-2.5",
                    exerciseTheme.loggedRow
                  )}
                >
                  <div
                    className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                      exerciseTheme.loggedIcon
                    )}
                  >
                    <Icon className="h-4 w-4 text-white" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={measureCardTitle}>{ex.name}</p>
                    <p className={measureCardMeta}>
                      {ex.duration} min · {ex.intensity}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Flame className={cn("h-3.5 w-3.5", exerciseTheme.icon)} />
                    <span className={cn(measureCardValueSubhead, exerciseTheme.estimateText)}>
                      {ex.calories}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
          {hasMore ? (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className={cn(
                "flex w-full items-center justify-center gap-1.5 rounded-xl border border-border/70 py-2.5 transition-colors hover:bg-muted/30",
                measureActionLabel,
                "text-muted-foreground"
              )}
            >
              {showAll ? (
                <>
                  View less
                  <ChevronUp className="h-4 w-4" strokeWidth={2} aria-hidden />
                </>
              ) : (
                <>
                  View more ({hiddenCount} more)
                  <ChevronDown className="h-4 w-4" strokeWidth={2} aria-hidden />
                </>
              )}
            </button>
          ) : null}
        </div>
      ) : (
        <p className={cn("px-0.5", measureCardMeta)}>No workouts logged yet today.</p>
      )}
    </div>
  );
};

export default ManualExerciseLogCard;
